/**
 * Who at a supplier receives RFQ emails (publish, request rounds, scope-change resends).
 *
 * Candidates mirror functions/src/rfqSupplierEmailContext.ts `loadSupplierEmailRecipients`:
 * the org's RFQ-notifications contact, every portal user linked to the org, and every
 * non-revoked supplier invite. Staff can narrow that per project on the supplier's
 * `TimberSupplierInvite.notifyEmails`; absent means everyone.
 */
import type { CRMUser, SupplierInvite, SupplierOrg, TimberSupplierInvite } from '../types';
import { parseSupplierPortalRole, supplierPortalRoleLabel } from './supplierWorkFlags';

export type SupplierRfqRecipientSource = 'rfq_contact' | 'portal_user' | 'invite';

export type SupplierRfqRecipientCandidate = {
  /** Lowercase, trimmed. */
  email: string;
  /** Person's name when known, else the email. */
  name: string;
  /** "RFQ notifications contact", "Owner", "Employee", "Invited · pending". */
  detail: string;
  source: SupplierRfqRecipientSource;
};

export function normalizeRecipientEmail(raw: unknown): string {
  const email = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return email.includes('@') ? email : '';
}

/** Everyone the RFQ email could go to for this org, deduped by email, in display order. */
export function supplierRfqRecipientCandidates(
  org: SupplierOrg | undefined | null,
  supplierInvites: SupplierInvite[],
  crmUsers: CRMUser[],
): SupplierRfqRecipientCandidate[] {
  if (!org?.id) return [];
  const out: SupplierRfqRecipientCandidate[] = [];
  const seen = new Set<string>();
  const push = (c: SupplierRfqRecipientCandidate) => {
    if (!c.email || seen.has(c.email)) return;
    seen.add(c.email);
    out.push(c);
  };

  const contact = normalizeRecipientEmail(org.defaultContactEmail);
  if (contact) {
    push({
      email: contact,
      name: org.defaultContactFirstName?.trim() || org.name,
      detail: 'RFQ notifications contact',
      source: 'rfq_contact',
    });
  }

  const users = crmUsers
    .filter(u => u.supplierOrgId === org.id && u.isActive !== false)
    .slice()
    .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email, undefined, { sensitivity: 'base' }));
  for (const u of users) {
    const email = normalizeRecipientEmail(u.email);
    if (!email) continue;
    push({
      email,
      name: u.displayName?.trim() || email,
      detail: supplierPortalRoleLabel(parseSupplierPortalRole(u.supplierPortalRole)),
      source: 'portal_user',
    });
  }

  for (const inv of supplierInvites) {
    if (inv.supplierOrgId !== org.id || inv.status === 'revoked') continue;
    const email = normalizeRecipientEmail(inv.email);
    if (!email) continue;
    push({
      email,
      name: inv.name?.trim() || email,
      detail: inv.status === 'accepted' ? 'Accepted invite' : 'Invited · pending',
      source: 'invite',
    });
  }
  return out;
}

/** Cleaned `notifyEmails` for storage; `undefined` when it would mean "everyone". */
export function normalizeNotifyEmails(
  selected: string[] | null | undefined,
  candidates: SupplierRfqRecipientCandidate[],
): string[] | undefined {
  if (!selected) return undefined;
  const chosen = Array.from(new Set(selected.map(normalizeRecipientEmail).filter(Boolean))).sort();
  const all = candidates.map(c => c.email).sort();
  if (chosen.length === all.length && chosen.every((e, i) => e === all[i])) return undefined;
  return chosen;
}

/**
 * Emails the server will actually send to: the candidates, narrowed to `notifyEmails`
 * when the invite has one. A narrowed list never adds addresses the org doesn't know.
 */
export function resolveSupplierRfqRecipientEmails(
  candidates: SupplierRfqRecipientCandidate[],
  invite: Pick<TimberSupplierInvite, 'notifyEmails'> | undefined | null,
): string[] {
  const all = candidates.map(c => c.email);
  const allow = (invite?.notifyEmails ?? []).map(normalizeRecipientEmail).filter(Boolean);
  if (!allow.length) return all;
  const allowSet = new Set(allow);
  return all.filter(e => allowSet.has(e));
}

export type SupplierRfqRecipientRow = {
  orgId: string;
  orgName: string;
  emails: string[];
  /** True when the invite narrows recipients below everyone. */
  narrowed: boolean;
  notificationFirstName: string;
};

/** Per-org recipient rows for the publish / resend review modal. */
export function resolveSupplierRfqRecipientRows(
  orgIds: string[],
  supplierOrgById: Map<string, SupplierOrg>,
  supplierInvites: SupplierInvite[],
  crmUsers: CRMUser[],
  timberSupplierInvites: TimberSupplierInvite[] | undefined,
): SupplierRfqRecipientRow[] {
  return orgIds.map(orgId => {
    const org = supplierOrgById.get(orgId);
    const candidates = supplierRfqRecipientCandidates(org, supplierInvites, crmUsers);
    const invite = timberSupplierInvites?.find(i => i.supplierOrgId === orgId);
    const emails = resolveSupplierRfqRecipientEmails(candidates, invite);
    return {
      orgId,
      orgName: org?.name || orgId,
      emails,
      narrowed: emails.length < candidates.length,
      notificationFirstName: (org?.defaultContactFirstName || '').trim(),
    };
  });
}
